/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Children, useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Box,
    Text,
    useStyleConfig,
    Grid
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

const DIRECTIONS = {
    FORWARD: 1,
    BACKWARD: -1
}

/**
 * SwatchGroup allows you to create a list of swatches
 * Each Swatch is a link with will direct to a href passed to them
 */
const SwatchGroup = (props) => {
    const {
        isFromProducSetDrawer = false,
        variant,
        ariaLabel,
        displayName,
        children,
        label = '',
        value,
        handleChange = noop
    } = props

    const styles = useStyleConfig('SwatchGroup', {variant, value})
    const [selectedIndex, setSelectedIndex] = useState(findIndexOfChildWithValue(value))
    const wrapperRef = useRef(null)

    // Handle keyboard navigation.
    const onKeyDown = useCallback(
        (e) => {
            const {key} = e
            const move = (direction = DIRECTIONS.FORWARD) => {
                let index = selectedIndex + direction // forward = +1 backwards = -1
                index = (selectedIndex + direction) % children.length // keep number in bounds of array with modulus
                index = index < 0 ? children.length - Math.abs(index) : Math.abs(index) // We we are dealing with a negative we have to invert the index

                // Get a reference to the newly selected swatch as we are going to focus it later.
                const swatchEl = wrapperRef?.current?.children[index]

                // Set the new index that is always in the arrays range.
                setSelectedIndex(index)

                // Behave like a radio button a focus the new swatch.
                swatchEl?.focus()
            }

            switch (key) {
                case 'ArrowUp':
                case 'ArrowLeft':
                    e.preventDefault()
                    move(DIRECTIONS.BACKWARD)
                    break
                case 'ArrowDown':
                case 'ArrowRight':
                    e.preventDefault()
                    move(DIRECTIONS.FORWARD)
                    break
                default:
                    break
            }
        },
        [selectedIndex]
    )

    // Finds child swatch that matches to the current variation, if any.
    // For example, if no size was selected, it would return -1 for the size swatch.
    function findIndexOfChildWithValue(value) {
        const childrenArray = Children.toArray(children ?? [])
        const index = childrenArray.findIndex(({props}) => props?.value === value)
        return index
    }

    // Initialize the component state on mount, this includes the selected index value.
    useEffect(() => {
        if (!value) {
            return
        }

        const childIndex = findIndexOfChildWithValue(value)
        setSelectedIndex(childIndex)
    }, [])

    // Whenever the selected index changes ensure that we call the change handler.
    useEffect(() => {
        if (selectedIndex === -1) return

        const childrenArray = Children.toArray(children)
        const newValue = childrenArray[selectedIndex]?.props?.value

        handleChange(newValue)
    }, [selectedIndex])

    return (
        <Box onKeyDown={onKeyDown}>
            <Flex {...styles.swatchGroup}>
                {label && !isFromProducSetDrawer && (
                    <Flex alignItems="center" flex="1" mb={10}>
                        <Text variant="bodySmall" color="darkGray">
                            <FormattedMessage
                                id="swatch_group.selected.label"
                                defaultMessage="{label}:"
                                values={{label}}
                            />
                        </Text>
                        <Text variant="bodySmall" ml={3}>
                            {label === 'Colour'
                                ? displayName.charAt(0).toUpperCase() +
                                  displayName.slice(1).toLowerCase()
                                : displayName}
                        </Text>
                    </Flex>
                )}
                <Grid as="ul" ref={wrapperRef} role="radiogroup" aria-label={ariaLabel || label} {...styles.swatchesWrapper}>
                    {Children.toArray(children).map((child, index) => {
                        const selected = child?.props?.value === value
                        return (
                            <Box
                                as="li"
                                key={child?.props?.value}
                                listStyleType="none"
                                display={'flex'}
                                width={'fit-content'}
                            >
                                {React.cloneElement(child, {
                                    handleSelect: handleChange,
                                    selected,
                                    isFocusable: value ? selected : index === 0
                                })}
                            </Box>
                        )
                    })}
                </Grid>
            </Flex>
        </Box>
    )
}

SwatchGroup.displayName = 'SwatchGroup'

SwatchGroup.propTypes = {
    /**
     * The aria label to be used on the group. If none is provided we will
     * use the label prop.
     */
    ariaLabel: PropTypes.string,
    /**
     * The attribute name of the swatch group. E.g color, size
     */
    label: PropTypes.string,
    /**
     * The display value of the selected option
     */
    displayName: PropTypes.string,
    /**
     * The Swatch options to choose between
     */
    children: PropTypes.node,
    /**
     * This function is called whenever the selected swatch changes.
     */
    handleChange: PropTypes.func,
    /**
     * The currentvalue for the option.
     */
    value: PropTypes.string,
    /**
     * The shape of the Swatch
     */
    variant: PropTypes.string,
    isFromProducSetDrawer: PropTypes.bool
}

export default SwatchGroup
